/**
 * E2. Рулетка, 37 секторов (0–36). Ставки: цвет (18/37, ×2), дюжина
 * (12/37, ×3), число (1/37, ×36). Преимущество заведения 1/37 ≈ 2,7 %.
 * Ставка не больше половины сбережений (maxBet = 0,5·money); вход в казино —
 * 1 % годового дохода, списывается вместе с первым кругом вечера (rounds === 0)
 * и не уводит деньги в минус. Без ставки (денег нет) — `noPlay()`.
 * Переключение фокуса года на «отдых» — дело движка/клиента (план, Task 9).
 * За вечер не больше ROULETTE_MAX_ROUNDS кругов: шестой круг — RangeError
 * («казино закрывается»), счётчик кругов продвигает клиент через
 * `rouletteAfterRound`. Тяга +3, при выигрыше ROULETTE_URGE_WIN (калибровка, см. ниже).
 * Доступна с 18 лет, legal, в городе.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome, Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { casinoAvailable, clampBet, expectancy, noPlay, yearlyIncome } from './common';

export type RouletteKind = 'color' | 'dozen' | 'number';

export interface RouletteState {
  /** Верхняя граница ставки: ROULETTE_MAX_BET_SHARE от денег. */
  maxBet: number;
  rounds: number;
  /** Вход в казино за вечер: ROULETTE_ENTRY_SHARE годового дохода. */
  entryFee: number;
}

export interface RouletteInput {
  bet: number;
  kind: RouletteKind;
  /** Цвет: 0 красное / 1 чёрное; дюжина: 0–2; число: 0–36. По умолчанию 0. */
  pick?: number;
}

export const ROULETTE_POCKETS = 37;
export const ROULETTE_MAX_ROUNDS = 5;
export const ROULETTE_MAX_BET_SHARE = 0.5;
export const ROULETTE_ENTRY_SHARE = 0.01;
export const ROULETTE_RTP = 36 / ROULETTE_POCKETS;
export const ROULETTE_URGE = 3;
/**
 * Тяга при выигрыше («горячая рука»). Калибровка плана 2b, Task 11: спад −4
 * идёт каждый год, поэтому одна игра в год держит тягу у нуля, и к порогу 60
 * ведёт только серия выигрышей; при 6,5 политика «раз в год при деньгах»
 * даёт 2–6 % жизней с состоянием `gambling` (при 6 — 0,3 %, при 7 — 10 %).
 */
export const ROULETTE_URGE_WIN = 6.5;

/** Множитель полного возврата по виду ставки. */
export const ROULETTE_PAYOUT: Record<RouletteKind, number> = { color: 2, dozen: 3, number: 36 };

/**
 * Красные сектора — нечётные 1..35 (18 штук), чёрные — чётные 2..36.
 * Ноль проигрывает цвету и дюжине, но ставка «число» на 0 выигрывает ровно на нём (1/37).
 */
export function rouletteWins(kind: RouletteKind, pick: number, pocket: number): boolean {
  switch (kind) {
    case 'number':
      return pocket === Math.max(0, Math.min(ROULETTE_POCKETS - 1, Math.trunc(pick)));
    case 'color':
      return pocket !== 0 && pocket % 2 === (pick === 1 ? 0 : 1);
    case 'dozen':
      return pocket !== 0 && Math.floor((pocket - 1) / 12) === Math.max(0, Math.min(2, Math.trunc(pick)));
  }
}

export function rouletteAfterRound(state: RouletteState): RouletteState {
  return { ...state, rounds: state.rounds + 1 };
}

export const roulette: MinigameDef<RouletteInput, RouletteState> = {
  id: 'roulette',
  section: 'E',
  triggers: [{ kind: 'envelope' }],
  available(life: Life, _ds: Dataset, law: GamblingLaw): boolean {
    return casinoAvailable(life, law);
  },
  setup(life: Life, ds: Dataset): RouletteState {
    return {
      maxBet: ROULETTE_MAX_BET_SHARE * Math.max(0, life.money),
      rounds: 0,
      entryFee: ROULETTE_ENTRY_SHARE * Math.max(0, yearlyIncome(ds, life)),
    };
  },
  play(state: RouletteState, input: RouletteInput, life: Life, rng: Rng): MinigameOutcome {
    if (state.rounds >= ROULETTE_MAX_ROUNDS) throw new RangeError('roulette: casino is closed for tonight');
    const bet = clampBet(Math.min(input.bet, state.maxBet), life.money);
    if (bet <= 0) return noPlay();
    const fee = state.rounds === 0 ? Math.min(Math.max(0, state.entryFee), Math.max(0, life.money - bet)) : 0;
    const pocket = rng.int(0, ROULETTE_POCKETS - 1);
    const won = rouletteWins(input.kind, input.pick ?? 0, pocket);
    const result = won ? bet * (ROULETTE_PAYOUT[input.kind] - 1) : -bet;
    return {
      moneyDelta: result - fee,
      urgeDelta: won ? ROULETTE_URGE_WIN : ROULETTE_URGE,
      logKey: won ? 'minigame.roulette.win' : 'minigame.roulette.lose',
      logParams: { amount: Math.round(Math.abs(result)), pocket, fee: Math.round(fee) },
    };
  },
  expected(): MinigameOutcome {
    return noPlay();
  },
  expectancyText: () => expectancy('roulette', ROULETTE_RTP),
};
